'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

const faqs = [
  {
    q: 'How much does EMS training cost in Islamabad?',
    a: 'Aura Wellness Clubs offers three membership tiers tailored to your specific goals. Every tier includes personal training, Miha Bodytec technology, body composition tracking, and all equipment. Please contact us for the most current pricing rates.'
  },
  {
    q: 'Do I need to sign a long-term contract?',
    a: 'No. Our memberships are month-to-month with no long-term lock-in. We only require a 30-day notice before cancellation, as we prefer to earn your loyalty through results rather than trapping you with contracts.'
  },
  {
    q: 'Can I pause my membership?',
    a: 'Yes. You may pause your membership for up to 30 days per calendar year for travel, illness, or other personal demands. Extended pauses can be arranged for extraordinary circumstances.'
  },
  {
    q: 'What if I can’t use all my sessions in a month?',
    a: 'Unused sessions roll over for one month on Metabolic Sculpt and Signature Sanctuary memberships. We prioritize consistent training over the pressure of busy periods.'
  },
  {
    q: 'Do I need to buy any gear or bring gym clothes?',
    a: 'No. Aura provides everything you need, including a specialized training suit, undergarments, towels, water, and post-workout protein. You can arrive in your regular clothes and leave the same way.'
  },
  {
    q: 'Is there a trial session available?',
    a: 'Yes. We offer a complimentary body composition analysis and introductory EMS session for all first-time visitors so you can experience the technology before committing.'
  },
  {
    q: 'Can I share my membership with a family member?',
    a: 'Memberships are individual because programs are personalized to each client’s specific body composition and goals. However, we do offer household discounts for families.'
  },
  {
    q: 'Are there discounts for paying upfront?',
    a: 'Yes. We offer meaningful savings for members who choose quarterly or annual payment options compared to standard monthly billing.'
  },
  {
    q: 'How does Aura differ from a traditional gym?',
    a: 'Traditional gyms depend on members who don’t show up. Aura works on the opposite principle: we accept only the number of members we can genuinely serve well with one-on-one supervised training and advanced technology.'
  },
  {
    q: 'Do you offer corporate wellness rates?',
    a: 'Yes. For teams of four or more, we provide discounted rates, group body composition assessments, and quarterly wellness reporting for HR departments.'
  }
];

export default function MembershipFaqSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 sm:py-24 bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 uppercase italic tracking-tighter leading-none" 
            style={{ color: GOLD_ACCENT, textShadow: `0 0 10px ${SHADOW_GOLD}` }}
          >
            Membership <span className="not-italic font-light text-white">FAQ</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 font-light">
            Everything you need to know about investing in your high-performance future.
          </p>
        </div>

        {/* Accordion List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div 
                key={index}
                className="rounded-xl overflow-hidden transition-all duration-300"
                style={{ 
                  background: isOpen ? '#111' : 'transparent',
                  border: `1px solid ${isOpen ? GOLD_ACCENT : '#222'}`,
                }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left focus:outline-none group"
                >
                  <span 
                    className={`text-base sm:text-lg font-bold transition-colors duration-300 ${isOpen ? 'text-[#faaf40]' : 'text-zinc-200 group-hover:text-white'}`}
                  >
                    {faq.q}
                  </span>
                  
                  <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                     <ChevronDown 
                        className="w-5 h-5" 
                        style={{ color: isOpen ? GOLD_ACCENT : '#555' }} 
                     />
                  </div>
                </button>

                <div 
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="p-5 sm:p-6 pt-0 text-sm sm:text-base text-zinc-400 leading-relaxed border-t border-white/5">
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Call to Action Link */}
        <div className="text-center mt-12">
           <p className="text-zinc-500 text-sm uppercase tracking-widest">
             Still have questions?{' '}
             <a href="/contact" className="text-[#faaf40] underline hover:text-white transition-colors">
               Speak with a consultant
             </a>
           </p>
        </div>

      </div>
    </section>
  );
}